/**
 * Web inactivity (idle) sign-out — per organization.
 *
 * The timeout used to be a hard-coded 8 hours in AuthContext. It is now the
 * active organization's `idle_timeout_minutes` from
 * GET /api/organizations/{cbo_id}/privacy-policy (set by an org admin in
 * Settings > Data Privacy), falling back to 480 minutes (= the old 8 hours).
 *
 * WHY THE VALUE AND THE LAST ACTIVITY LIVE IN localStorage
 * Every window of the app — the main window, a pop-out window, and each
 * same-origin iframe tab inside a tabbed pop-out (/popout/page) — mounts its own
 * AuthProvider with its own activity listeners and its own timer. They share one
 * sign-in (the token is in localStorage), so they must also share one idle
 * clock: each window writes `session_start` on activity, and each timer counts
 * from the LATEST activity any window saw. Otherwise a user working in a tab
 * would be signed out by the main window, which saw no mouse events of its own.
 * The configured minutes are cached under one key for the same reason, so every
 * window enforces the same limit, including on a cold load before the policy
 * request returns.
 *
 * Pure functions only; AuthContext wires them to timers and storage.
 */

export const DEFAULT_IDLE_TIMEOUT_MINUTES = 480;
export const MIN_IDLE_TIMEOUT_MINUTES = 5;
export const MAX_IDLE_TIMEOUT_MINUTES = 720;

/** localStorage: last activity (ms epoch) in ANY window. Existing key, kept. */
export const SESSION_ACTIVITY_KEY = 'session_start';
/** localStorage: the active org's idle timeout in minutes, shared by all windows. */
export const IDLE_TIMEOUT_STORAGE_KEY = 'session_idle_timeout_minutes';
/** window event: an admin just saved the org's timeout; AuthContext reloads it. */
export const IDLE_TIMEOUT_CHANGED_EVENT = 'weavein:idle-timeout-changed';

/** The normal warning lead time: 2 minutes before sign-out. */
const WARNING_LEAD_SECONDS = 120;
/** Below this timeout the warning is a fraction of it instead of a flat 2 minutes. */
const SHORT_TIMEOUT_SECONDS = 10 * 60;
const SHORT_TIMEOUT_WARNING_FRACTION = 0.2;

/** A usable timeout in minutes; anything missing or out of range -> the default. */
export function normalizeIdleTimeoutMinutes(value: unknown): number {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_IDLE_TIMEOUT_MINUTES;
  const minutes = Math.round(n);
  if (minutes < MIN_IDLE_TIMEOUT_MINUTES || minutes > MAX_IDLE_TIMEOUT_MINUTES) {
    return DEFAULT_IDLE_TIMEOUT_MINUTES;
  }
  return minutes;
}

/** True when an admin-entered value is acceptable to save. */
export function isValidIdleTimeoutMinutes(value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_IDLE_TIMEOUT_MINUTES &&
    value <= MAX_IDLE_TIMEOUT_MINUTES
  );
}

/**
 * Seconds before sign-out at which the warning appears: 2 minutes, or 20% of
 * the timeout when the timeout is under 10 minutes (a 5-minute timeout warns at
 * 1 minute rather than spending 40% of the session on a warning banner).
 */
export function warningLeadSeconds(timeoutSeconds: number): number {
  if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) return 0;
  if (timeoutSeconds < SHORT_TIMEOUT_SECONDS) {
    return Math.max(1, Math.ceil(timeoutSeconds * SHORT_TIMEOUT_WARNING_FRACTION));
  }
  return WARNING_LEAD_SECONDS;
}

export function shouldShowIdleWarning(remainingSeconds: number, timeoutSeconds: number): boolean {
  return remainingSeconds > 0 && remainingSeconds <= warningLeadSeconds(timeoutSeconds);
}

/** The latest of this window's activity and the shared (any-window) activity. */
export function latestActivityMs(localMs: number, sharedMs: number | null | undefined): number {
  return typeof sharedMs === 'number' && Number.isFinite(sharedMs) && sharedMs > localMs ? sharedMs : localMs;
}

/** Whole seconds left before an idle sign-out, never negative. */
export function idleRemainingSeconds(lastActivityMs: number, nowMs: number, timeoutMinutes: number): number {
  const timeoutSeconds = timeoutMinutes * 60;
  const elapsed = Math.floor((nowMs - lastActivityMs) / 1000);
  return Math.max(0, timeoutSeconds - Math.max(0, elapsed));
}

export function isIdleExpired(lastActivityMs: number, nowMs: number, timeoutMinutes: number): boolean {
  return nowMs - lastActivityMs >= timeoutMinutes * 60 * 1000;
}

// ------------------------------------------------------------ storage access

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readSharedActivityMs(storage: Storage | null = safeStorage()): number | null {
  try {
    const raw = storage?.getItem(SESSION_ACTIVITY_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function readCachedIdleTimeoutMinutes(storage: Storage | null = safeStorage()): number {
  try {
    return normalizeIdleTimeoutMinutes(storage?.getItem(IDLE_TIMEOUT_STORAGE_KEY));
  } catch {
    return DEFAULT_IDLE_TIMEOUT_MINUTES;
  }
}

export function writeCachedIdleTimeoutMinutes(minutes: number, storage: Storage | null = safeStorage()): void {
  try {
    storage?.setItem(IDLE_TIMEOUT_STORAGE_KEY, String(normalizeIdleTimeoutMinutes(minutes)));
  } catch {
    /* storage unavailable: the in-memory value still applies to this window */
  }
}
